import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/Dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import {
  Truck,
  CheckCircle,
  AlertCircle,
  Loader2,
  MapPin,
  Users,
  Shield,
  Clock,
  Info,
  RefreshCw,
  X
} from 'lucide-react';
import { apiService, authService } from '../../services';
import { getAmbulanceTypeAbbreviation } from '../../utils/ambulanceTypeHelper';

// Admin-scoped counterpart to Fleet's own AmbulanceRegistrationModal.jsx
// (src/components/fleet/) — that one derives operator_id from the logged-in
// fleet user and can't register a vehicle for anyone else's fleet. This is
// the "All Vehicles" tab's actual Add Vehicle form — previously that tab
// (and Bulk Register) silently fell through to just showing the vehicle
// list again, with no way to add one at all.
const AdminAmbulanceRegistrationModal = ({ onSuccess, onError, children, inline = false, onCancel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [registeredAmbulance, setRegisteredAmbulance] = useState(null);
  const [fleetOperators, setFleetOperators] = useState([]);
  const [ambulanceTypes, setAmbulanceTypes] = useState([]);
  const [drivers, setDrivers] = useState([]);

  const [formData, setFormData] = useState({
    operator_id: '',
    plate_number: '',
    type_id: '',
    assigned_driver_id: '',
    current_latitude: '',
    current_longitude: '',
    status: 'AVAILABLE',
  });

  const [errors, setErrors] = useState({});

  // Fleet operators + ambulance types on open — same two independent lists
  // every vehicle needs regardless of which operator ends up selected.
  useEffect(() => {
    if (!isOpen && !inline) return;
    const loadInitialData = async () => {
      setIsInitializing(true);
      try {
        const [operatorsRes, typesRes] = await Promise.allSettled([
          authService.getAllFleetOperatorsUnpaginated(),
          apiService.getAmbulanceTypes(),
        ]);
        if (operatorsRes.status === 'fulfilled') {
          const val = operatorsRes.value;
          const list = Array.isArray(val?.data) ? val.data : (Array.isArray(val) ? val : []);
          setFleetOperators(list);
        } else {
          setFleetOperators([]);
        }
        if (typesRes.status === 'fulfilled') {
          const typesData = typesRes.value;
          const typesList = Array.isArray(typesData?.data) ? typesData.data : (Array.isArray(typesData) ? typesData : []);
          setAmbulanceTypes(typesList);
        } else {
          setAmbulanceTypes([]);
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
        onError?.('Failed to load fleet operators or ambulance types');
      } finally {
        setIsInitializing(false);
      }
    };
    loadInitialData();
  }, [isOpen, inline]);

  // Available drivers are scoped to whichever operator is currently
  // selected — assigning a driver from a different fleet than the vehicle
  // itself wouldn't make sense, and re-fetches every time that changes.
  useEffect(() => {
    if (!formData.operator_id) { setDrivers([]); return; }
    let cancelled = false;
    setLoadingDrivers(true);
    apiService.getDrivers({ operator_id: formData.operator_id, limit: 100 })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setDrivers(list);
      })
      .catch(() => { if (!cancelled) setDrivers([]); })
      .finally(() => { if (!cancelled) setLoadingDrivers(false); });
    return () => { cancelled = true; };
  }, [formData.operator_id]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => {
      // Switching operator invalidates whatever driver was picked for the
      // previous one — the drivers list is about to be refetched anyway.
      if (field === 'operator_id' && value !== prev.operator_id) {
        return { ...prev, [field]: value, assigned_driver_id: '' };
      }
      return { ...prev, [field]: value };
    });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [errors]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.operator_id) {
      newErrors.operator_id = 'Please select a fleet operator';
    }

    if (!formData.plate_number.trim()) {
      newErrors.plate_number = 'Plate number is required';
    } else if (!/^[A-Z]{2}[0-9]{2}[A-Z]{2}[0-9]{4}$/.test(formData.plate_number.trim())) {
      newErrors.plate_number = 'Please enter a valid Indian vehicle plate number (e.g., MH12AB1234)';
    }

    if (!formData.type_id) {
      newErrors.type_id = 'Ambulance type is required';
    }

    if (formData.current_latitude && (isNaN(formData.current_latitude) || formData.current_latitude < -90 || formData.current_latitude > 90)) {
      newErrors.current_latitude = 'Please enter a valid latitude (-90 to 90)';
    }

    if (formData.current_longitude && (isNaN(formData.current_longitude) || formData.current_longitude < -180 || formData.current_longitude > 180)) {
      newErrors.current_longitude = 'Please enter a valid longitude (-180 to 180)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const submitData = {
        plate_number: formData.plate_number.trim().toUpperCase(),
        type_id: formData.type_id,
        assigned_driver_id: formData.assigned_driver_id || null,
        current_latitude: formData.current_latitude ? parseFloat(formData.current_latitude) : null,
        current_longitude: formData.current_longitude ? parseFloat(formData.current_longitude) : null,
        status: formData.status,
        operator_id: formData.operator_id,
      };

      const result = await apiService.registerAmbulance(submitData);

      setRegisteredAmbulance({
        ...result.data,
        plate_number: formData.plate_number,
        type_name: ambulanceTypes.find(t => t.type_id === formData.type_id)?.name,
        driver_name: drivers.find(d => d.driver_id === formData.assigned_driver_id)?.name,
        operator_name: fleetOperators.find(o => (o.id || o.operator_id) === formData.operator_id)?.name,
      });

      setShowSuccess(true);
      onSuccess?.(result.data);
    } catch (error) {
      console.error('Registration error:', error);
      onError?.(error.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (inline && onCancel) {
      onCancel();
    } else {
      setIsOpen(false);
    }
    setShowSuccess(false);
    setRegisteredAmbulance(null);
    setFormData({
      operator_id: '',
      plate_number: '',
      type_id: '',
      assigned_driver_id: '',
      current_latitude: '',
      current_longitude: '',
      status: 'AVAILABLE',
    });
    setErrors({});
  };

  const getStatusBadge = (status) => {
    const colors = {
      AVAILABLE: "bg-green-100 text-green-800",
      BUSY: "bg-yellow-100 text-yellow-800",
      MAINTENANCE: "bg-red-100 text-red-800",
      OFFLINE: "bg-gray-100 text-gray-800"
    };
    return (
      <Badge className={colors[status] || "bg-gray-100 text-gray-800"}>
        {status}
      </Badge>
    );
  };

  const getSelectedTypeName = () => {
    if (!formData.type_id || !ambulanceTypes.length) return null;
    const selectedType = ambulanceTypes.find(type => type.type_id === formData.type_id);
    return selectedType?.abbreviation || getAmbulanceTypeAbbreviation(selectedType?.name) || selectedType?.name || null;
  };

  if (showSuccess && registeredAmbulance) {
    return (
      <div className="bg-gradient-to-br from-green-50 to-blue-50 min-h-[500px] flex items-center justify-center p-6">
        <Card className="w-full max-w-2xl shadow-2xl border-0">
          <CardContent className="p-8 text-center">
            <div className="mb-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Ambulance Registered Successfully!</h2>
              <p className="text-gray-600">The ambulance has been added to {registeredAmbulance.operator_name || 'the'} fleet.</p>
            </div>

            <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ambulance Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-blue-600" />
                    <span className="font-medium">Plate Number:</span>
                    <span className="font-mono bg-gray-100 px-2 py-1 rounded">{registeredAmbulance.plate_number}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-blue-600" />
                    <span className="font-medium">Type:</span>
                    <span>{registeredAmbulance.type_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-blue-600" />
                    <span className="font-medium">Fleet Operator:</span>
                    <span>{registeredAmbulance.operator_name || '—'}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-600" />
                    <span className="font-medium">Driver:</span>
                    <span>{registeredAmbulance.driver_name || 'Not assigned'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="font-medium">Status:</span>
                    {getStatusBadge(registeredAmbulance.status)}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={handleClose}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
              >
                Close
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formContent = (
    <div className="space-y-6">
      {/* Fleet Operator Selection */}
      <Card className="border-blue-100 bg-blue-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Truck className="w-4 h-4 text-blue-600" />
            Assign Fleet Operator
          </CardTitle>
          <CardDescription>Select which fleet operator this ambulance belongs to</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Fleet Operator *</label>
            {isInitializing ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading operators...
              </div>
            ) : (
              <Select
                value={formData.operator_id}
                onValueChange={(value) => handleInputChange('operator_id', value)}
              >
                <SelectTrigger className={errors.operator_id ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select Fleet Operator" />
                </SelectTrigger>
                <SelectContent>
                  {fleetOperators.map((operator) => (
                    <SelectItem key={operator.id || operator.operator_id} value={operator.id || operator.operator_id}>
                      {operator.name} ({operator.city || 'Unknown City'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {errors.operator_id && <p className="text-red-500 text-xs">{errors.operator_id}</p>}
          </div>
        </CardContent>
      </Card>

      {/* Basic Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Truck className="w-5 h-5 text-blue-600" />
            Basic Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Plate Number */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plate Number *
              </label>
              <Input
                value={formData.plate_number}
                onChange={(e) => handleInputChange('plate_number', e.target.value.toUpperCase())}
                placeholder="Enter plate number"
                className={`${errors.plate_number ? 'border-red-500' : 'border-gray-300'} focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.plate_number && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.plate_number}
                </p>
              )}
            </div>

            {/* Ambulance Type */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ambulance Type *
              </label>
              {isInitializing ? (
                <div className="flex items-center justify-center p-4 border border-gray-300 rounded-md">
                  <RefreshCw className="w-4 h-4 animate-spin text-blue-600 mr-2" />
                  <span className="text-gray-600">Loading types...</span>
                </div>
              ) : (
                <Select
                  value={formData.type_id}
                  onValueChange={(value) => handleInputChange('type_id', value)}
                >
                  <SelectTrigger className={`${errors.type_id ? 'border-red-500' : 'border-gray-300'} focus:ring-blue-500 focus:border-blue-500`}>
                    <SelectValue placeholder="Select ambulance type">
                      {getSelectedTypeName()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {ambulanceTypes.map((type) => (
                      <SelectItem key={type.type_id} value={type.type_id}>
                        <div className="flex items-center justify-between w-full" title={type.name}>
                          <div className="font-semibold text-gray-900">{type.abbreviation || getAmbulanceTypeAbbreviation(type.name) || type.name}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {errors.type_id && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.type_id}
                </p>
              )}
            </div>

            {/* Driver Assignment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assign Driver
              </label>
              <Select
                value={formData.assigned_driver_id}
                onValueChange={(value) => handleInputChange('assigned_driver_id', value)}
                disabled={!formData.operator_id}
              >
                <SelectTrigger className="border-gray-300 focus:ring-blue-500 focus:border-blue-500">
                  <SelectValue placeholder={
                    !formData.operator_id ? 'Select a fleet operator first' : loadingDrivers ? 'Loading drivers…' : 'Select driver (optional)'
                  } />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No driver assigned</SelectItem>
                  {drivers.map((driver) => (
                    <SelectItem key={driver.driver_id} value={driver.driver_id}>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        <span>{driver.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {driver.license_number}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Initial Status
              </label>
              <Select
                value={formData.status}
                onValueChange={(value) => handleInputChange('status', value)}
              >
                <SelectTrigger className="border-gray-300 focus:ring-blue-500 focus:border-blue-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AVAILABLE">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>Available</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="BUSY">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                      <span>Busy</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="MAINTENANCE">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <span>Under Maintenance</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="OFFLINE">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                      <span>Offline</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Location Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <MapPin className="w-5 h-5 text-green-600" />
            Location Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-900 mb-1">GPS Coordinates</p>
                <p className="text-xs text-blue-700">
                  Enter the current location coordinates for real-time tracking.
                  You can leave these empty and update them later.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Latitude */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Latitude
              </label>
              <Input
                type="number"
                step="any"
                placeholder="19.0760"
                value={formData.current_latitude}
                onChange={(e) => handleInputChange('current_latitude', e.target.value)}
                className={`${errors.current_latitude ? 'border-red-500' : 'border-gray-300'} focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.current_latitude && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.current_latitude}
                </p>
              )}
            </div>

            {/* Longitude */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Longitude
              </label>
              <Input
                type="number"
                step="any"
                placeholder="72.8777"
                value={formData.current_longitude}
                onChange={(e) => handleInputChange('current_longitude', e.target.value)}
                className={`${errors.current_longitude ? 'border-red-500' : 'border-gray-300'} focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.current_longitude && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.current_longitude}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={isLoading}
          className="flex-1 sm:flex-none"
        >
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isLoading || isInitializing}
          className="flex-1 sm:flex-none bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-200"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Registering...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Register Ambulance
            </>
          )}
        </Button>
      </div>
    </div>
  );

  if (inline) {
    return (
      <div className="max-w-4xl">
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mb-4">
          <h2 className="text-lg sm:text-xl font-bold mb-2 flex items-center gap-2">
            <Truck className="w-5 h-5 text-blue-600" />
            Register New Ambulance
          </h2>
          <p className="text-sm text-gray-600">Add a new ambulance to any fleet. All fields marked with * are required.</p>
        </div>
        <form onSubmit={handleSubmit}>
          {formContent}
        </form>
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-200">
            <Truck className="w-4 h-4 mr-2" />
            Register Ambulance
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Truck className="w-6 h-6 text-blue-600" />
            Register New Ambulance
          </DialogTitle>
          <DialogDescription className="text-gray-600 pb-4">
            Add a new ambulance to any fleet operator, with complete information.
          </DialogDescription>
        </DialogHeader>
        <div className="p-6 pt-0 max-h-[80vh] overflow-y-auto">
          <form onSubmit={handleSubmit}>
            {formContent}
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminAmbulanceRegistrationModal;
